// Canonical club registry — pure data + name resolution. No DOM, no AWS.
//
// Ported verbatim from the legacy js/clubs.js registry. The DOM/canvas
// name-fitting helpers (fitTeamName, fitTeamNamesInFragment) intentionally
// stay in the legacy file — they are presentation concerns and become React
// hooks in migration Phase 3. Only the pure parts live here, because both the
// backend (app-api, shaping per-widget cache) and the frontend need them.
//
// WHY THE ID IS A 3-LETTER CODE (placeholder): the real API will key clubs by
// a numeric/string ID (e.g. 387774). When that lands, re-key CLUBS and update
// fixture-select values; every lookup already resolves through CLUBS[id], so
// nothing else changes. `ClubId` is a string alias so that migration is typed.

/** Canonical club identifier. A 3-letter short code today; a numeric/string API id later. */
export type ClubId = string;

export interface Club {
  /** Full official name — league tables, detailed views. */
  name: string;
  /** Compact name — mobile top bar, controls. */
  medium: string;
  /** Short code — tight spaces, column headers. */
  short: string;
  /** Primary brand colour (hex). */
  colour: string;
}

/** A resolved club: its registry entry plus the canonical id it resolved to. */
export type ResolvedClub = Club & { id: ClubId };

// Single source of truth. Every display name / colour / short code lives here.
export const CLUBS: Record<ClubId, Club> = {
  ARS: { name: "Arsenal", medium: "Arsenal", short: "ARS", colour: "#EF0107" },
  AVL: { name: "Aston Villa", medium: "A. Villa", short: "AVL", colour: "#95012c" },
  BOU: { name: "Bournemouth", medium: "Bournemouth", short: "BOU", colour: "#B50E12" },
  BRE: { name: "Brentford", medium: "Brentford", short: "BRE", colour: "#E30613" },
  BHA: { name: "Brighton & Hove Albion", medium: "Brighton", short: "BHA", colour: "#0057B8" },
  BUR: { name: "Burnley", medium: "Burnley", short: "BUR", colour: "#6C1D45" },
  CHE: { name: "Chelsea", medium: "Chelsea", short: "CHE", colour: "#034694" },
  CRY: { name: "Crystal Palace", medium: "Palace", short: "CRY", colour: "#1B458F" },
  EVE: { name: "Everton", medium: "Everton", short: "EVE", colour: "#003399" },
  FUL: { name: "Fulham", medium: "Fulham", short: "FUL", colour: "#CC0000" },
  IPS: { name: "Ipswich Town", medium: "Ipswich", short: "IPS", colour: "#0033A0" },
  LEE: { name: "Leeds United", medium: "Leeds", short: "LEE", colour: "#FFCD00" },
  LEI: { name: "Leicester City", medium: "Leicester", short: "LEI", colour: "#003090" },
  LIV: { name: "Liverpool", medium: "Liverpool", short: "LIV", colour: "#C8102E" },
  LUT: { name: "Luton Town", medium: "Luton", short: "LUT", colour: "#F78F1E" },
  MCI: { name: "Manchester City", medium: "Man City", short: "MCI", colour: "#6CABDD" },
  MUN: { name: "Manchester United", medium: "Man Utd", short: "MUN", colour: "#DA291C" },
  MID: { name: "Middlesbrough", medium: "Middlesbro'", short: "MID", colour: "#E00000" },
  NEW: { name: "Newcastle United", medium: "Newcastle", short: "NEW", colour: "#555555" },
  NOR: { name: "Norwich City", medium: "Norwich", short: "NOR", colour: "#00A650" },
  NFO: { name: "Nottingham Forest", medium: "N. Forest", short: "NFO", colour: "#DD0000" },
  SHU: { name: "Sheffield United", medium: "Sheff Utd", short: "SHU", colour: "#EE2737" },
  SOU: { name: "Southampton", medium: "Southampton", short: "SOU", colour: "#D71920" },
  SUN: { name: "Sunderland", medium: "Sunderland", short: "SUN", colour: "#EB172B" },
  TOT: { name: "Tottenham Hotspur", medium: "Spurs", short: "TOT", colour: "#4a6fad" },
  WAT: { name: "Watford", medium: "Watford", short: "WAT", colour: "#FBEE23" },
  WHU: { name: "West Ham United", medium: "West Ham", short: "WHU", colour: "#7A263A" },
  WOL: { name: "Wolverhampton Wanderers", medium: "Wolves", short: "WOL", colour: "#FDB913" },
  // Championship / promoted clubs (BenBet W1). Colours = each club's well-known
  // primary; black/white kits use a mid-grey (like NEW) so bars stay readable on
  // both themes. Ben to review — docs/QUESTIONS.md Q-001.
  BIR: { name: "Birmingham City", medium: "Birmingham", short: "BIR", colour: "#0055A5" },
  BLB: { name: "Blackburn Rovers", medium: "Blackburn", short: "BLB", colour: "#009EE0" },
  BOL: { name: "Bolton Wanderers", medium: "Bolton", short: "BOL", colour: "#263C7E" },
  BRC: { name: "Bristol City", medium: "Bristol C.", short: "BRC", colour: "#E21A23" },
  CAR: { name: "Cardiff City", medium: "Cardiff", short: "CAR", colour: "#0070B5" },
  CHA: { name: "Charlton Athletic", medium: "Charlton", short: "CHA", colour: "#D4021D" },
  COV: { name: "Coventry City", medium: "Coventry", short: "COV", colour: "#6CBBE6" },
  DER: { name: "Derby County", medium: "Derby", short: "DER", colour: "#6E6E6E" },
  HUL: { name: "Hull City", medium: "Hull", short: "HUL", colour: "#F5A12D" },
  LIN: { name: "Lincoln City", medium: "Lincoln", short: "LIN", colour: "#E2001A" },
  MIL: { name: "Millwall", medium: "Millwall", short: "MIL", colour: "#001D5E" },
  OXF: { name: "Oxford United", medium: "Oxford", short: "OXF", colour: "#FFD300" },
  POR: { name: "Portsmouth", medium: "Portsmouth", short: "POR", colour: "#001489" },
  PNE: { name: "Preston North End", medium: "Preston", short: "PNE", colour: "#0A2240" },
  QPR: { name: "Queens Park Rangers", medium: "QPR", short: "QPR", colour: "#1D5BA4" },
  SHW: { name: "Sheffield Wednesday", medium: "Sheff Wed", short: "SHW", colour: "#2F4A9E" },
  STK: { name: "Stoke City", medium: "Stoke", short: "STK", colour: "#E03A3E" },
  SWA: { name: "Swansea City", medium: "Swansea", short: "SWA", colour: "#8A8A8A" },
  WBA: { name: "West Bromwich Albion", medium: "West Brom", short: "WBA", colour: "#122F67" },
  WRE: { name: "Wrexham", medium: "Wrexham", short: "WRE", colour: "#D71920" },
};

// Resolves any known name variant to a canonical club id. Universal translator
// for the mixed naming conventions in legacy data / fixture-select values.
// Add entries whenever a new name format shows up; remove once callers use ids.
export const CLUB_ALIAS: Record<string, ClubId> = {
  // Full official names
  Arsenal: "ARS",
  "Aston Villa": "AVL",
  Bournemouth: "BOU",
  Brentford: "BRE",
  "Brighton & Hove Albion": "BHA",
  Burnley: "BUR",
  Chelsea: "CHE",
  "Crystal Palace": "CRY",
  Everton: "EVE",
  Fulham: "FUL",
  "Ipswich Town": "IPS",
  "Leeds United": "LEE",
  "Leicester City": "LEI",
  Liverpool: "LIV",
  "Luton Town": "LUT",
  "Manchester City": "MCI",
  "Manchester United": "MUN",
  Middlesbrough: "MID",
  "Newcastle United": "NEW",
  "Norwich City": "NOR",
  "Nottingham Forest": "NFO",
  "Sheffield United": "SHU",
  Southampton: "SOU",
  Sunderland: "SUN",
  "Tottenham Hotspur": "TOT",
  Watford: "WAT",
  "West Ham United": "WHU",
  "Wolverhampton Wanderers": "WOL",
  Wolverhampton: "WOL",
  // Championship / promoted clubs (BenBet W1)
  "Birmingham City": "BIR",
  "Blackburn Rovers": "BLB",
  "Bolton Wanderers": "BOL",
  "Bristol City": "BRC",
  "Cardiff City": "CAR",
  "Charlton Athletic": "CHA",
  "Coventry City": "COV",
  "Derby County": "DER",
  "Hull City": "HUL",
  "Lincoln City": "LIN",
  Millwall: "MIL",
  "Oxford United": "OXF",
  Portsmouth: "POR",
  "Preston North End": "PNE",
  "Queens Park Rangers": "QPR",
  "Sheffield Wednesday": "SHW",
  "Stoke City": "STK",
  "Swansea City": "SWA",
  "West Bromwich Albion": "WBA",
  Wrexham: "WRE",

  // Informal / shorthand (current data.js + fixture-select values)
  "AFC Bournemouth": "BOU", // the data server's canonical name for Bournemouth (WS-C)
  Brighton: "BHA",
  Ipswich: "IPS",
  Leeds: "LEE",
  Leicester: "LEI",
  Luton: "LUT",
  Newcastle: "NEW",
  Norwich: "NOR",
  Palace: "CRY",
  Spurs: "TOT",
  Tottenham: "TOT",
  Villa: "AVL",
  "West Ham": "WHU",
  Wolves: "WOL",
  Birmingham: "BIR",
  Blackburn: "BLB",
  Bolton: "BOL",
  Cardiff: "CAR",
  Charlton: "CHA",
  Coventry: "COV",
  Derby: "DER",
  Hull: "HUL",
  Lincoln: "LIN",
  Oxford: "OXF",
  Preston: "PNE",
  QPR: "QPR",
  "Sheff Wed": "SHW",
  Stoke: "STK",
  Swansea: "SWA",
  "West Brom": "WBA",

  // Medium forms (in case they appear as data keys)
  "Man City": "MCI",
  "Man Utd": "MUN",
  "Man United": "MUN",
  "Sheff Utd": "SHU",
  "Nott'm Forest": "NFO",
};

/**
 * Resolve any known club name string (or a canonical id) to its full entry.
 * Returns the entry plus the `id` it resolved to, or null if unknown.
 *
 *   clubFromName("Tottenham")         → { id: "TOT", name: "Tottenham Hotspur", ... }
 *   clubFromName("Manchester United") → { id: "MUN", name: "Manchester United", ... }
 *   clubFromName("MUN")               → { id: "MUN", ... }   (already an id)
 *   clubFromName("unknown club")      → null
 */
export function clubFromName(name: string): ResolvedClub | null {
  const id = CLUB_ALIAS[name] ?? name;
  const club = CLUBS[id];
  return club ? { id, ...club } : null;
}
